import { useMemo } from "react";
import { Grid, FormGroup } from "@hawkins/components";
import {
  DropdownFormControl,
  FormProvider,
  InputFormControl,
} from "@hawkins/forms";

import { useContactUsForm } from "../../../hooks/useContactUsForm";
import {
  ContactUsForm as ContactUsFormType,
  ContactUsPage,
} from "../../../graphql/graphql";
import { M1Button } from "../../molecules/M1Button/M1Button";
import { toGoogleFormEntryPayload } from "../../../utils/googleFormPost";

import styles from "./ContactUsPage.module.scss";

interface ContactUsFormProps {
  formData: {
    items: ContactUsFormType[];
  };
  pageData: ContactUsPage | null;
  isGoogleFormSubmitting: boolean;
  submitGoogleForm: (data: Record<string, string>) => void;
}

export const ContactUsForm = ({
  formData,
  pageData,
  submitGoogleForm,
  isGoogleFormSubmitting,
}: ContactUsFormProps) => {
  const contactUsFormContent = useMemo(() => {
    return (
      formData?.items
        ?.slice()
        .sort((a, b) => Number(a?.order) - Number(b?.order)) || []
    );
  }, [formData]);

  const form = useContactUsForm(formData?.items);

  const onSubmit = (data: Record<string, string>) => {
    const formattedData = Object.entries(data).reduce(
      (acc, [key, v]) => {
        return {
          ...acc,
          [key]: typeof v === "object" ? (v as { value: string }).value : v,
        };
      },
      {} as Record<string, string>,
    );
    const googleFormData = toGoogleFormEntryPayload(formattedData);
    submitGoogleForm(googleFormData);
  };

  return (
    <div className={styles.contactUsForm}>
      <FormProvider {...form}>
        <FormGroup>
          <Grid columns={{ xs: 1, sm: 2 }}>
            {contactUsFormContent.map((formFieldDetails: ContactUsFormType) => {
              if (formFieldDetails?.type === "dropdown") {
                return (
                  <Grid.Item key={formFieldDetails.id}>
                    <DropdownFormControl
                      label={formFieldDetails?.field || ""}
                      name={formFieldDetails?.id || ""}
                      placeholder={formFieldDetails?.placeholder || ""}
                      options={formFieldDetails?.options || []}
                      requirementType={
                        formFieldDetails?.required === "true"
                          ? "required-star"
                          : "optional"
                      }
                    />
                  </Grid.Item>
                );
              }
              return (
                <Grid.Item
                  key={formFieldDetails.id}
                  colSpan={{
                    xs: 1,
                    sm: formFieldDetails?.type === "textarea" ? 2 : 1,
                  }}
                >
                  <InputFormControl
                    label={formFieldDetails?.field || ""}
                    name={formFieldDetails?.id || ""}
                    placeholder={formFieldDetails?.placeholder || ""}
                    requirementType={
                      formFieldDetails?.required === "true"
                        ? "required-star"
                        : "optional"
                    }
                    multiline={
                      formFieldDetails?.type === "textarea" ? true : false
                    }
                    rows={formFieldDetails?.type === "textarea" ? 3 : 1}
                  />
                </Grid.Item>
              );
            })}
          </Grid>
          <div className={styles.buttonContainer}>
            <M1Button
              variant="primary"
              size="large"
              onClick={form.handleSubmit(onSubmit)}
              className={styles.submitButton}
              disabled={isGoogleFormSubmitting}
            >
              {pageData?.submitButtonLabel || "Contact us"}
            </M1Button>
          </div>
        </FormGroup>
      </FormProvider>
    </div>
  );
};
